import { useMemo, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { SearchInput, Switch } from "@/components/ui/Form.jsx";
import { Avatar, Callout, EmptyState, StatCard, Tabs } from "@/components/ui/Display.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import ScheduleFormDrawer from "@/components/schedules/ScheduleFormDrawer.jsx";
import ShiftTimeline from "@/components/schedules/ShiftTimeline.jsx";

import { trackingScheduleService, userService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { useAuth } from "@/context/auth.js";
import {
    DAYS,
    DAY_LABEL,
    describeShift,
    formatClock,
    formatSpan,
    isOvernight,
} from "@/lib/schedule.js";
import { cx } from "@/lib/cx.js";

/*
 * Replaces frontend/app/(dashboard)/tracking-schedule/page.js — 2,032 lines.
 *
 * A schedule decides whether the desktop app records at all, so a wrong one
 * means no data and nothing saying why. Two things this page is careful about:
 *
 *   NET WORKING is not the shift span. Paid breaks stay inside the target,
 *   unpaid ones come out. Subtracting all of them makes a team look behind.
 *
 *   OVERNIGHT shifts work. They used to be impossible to save, and the split
 *   that tells the desktop when to track emitted nothing for them — a schedule
 *   that looked configured and tracked zero. See backend/utils/scheduleTime.js.
 */

const NONE = [];

export default function Schedules() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();
    const { can } = useAuth();

    const [tab, setTab] = useState("global");
    const [search, setSearch] = useState("");
    const [activeOnly, setActiveOnly] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formError, setFormError] = useState("");

    const [schedulesQuery, usersQuery] = useQueries({
        queries: [
            {
                queryKey: ["tracking-schedules"],
                queryFn: () =>
                    trackingScheduleService.getSchedules().then(
                        (r) => r.data?.data?.trackingSchedules ?? r.data?.data?.schedules ?? [],
                    ),
            },
            {
                queryKey: ["users"],
                queryFn: () => userService.getAllUsers().then((r) => r.data?.data?.users ?? []),
            },
        ],
    });

    const schedules = schedulesQuery.data ?? NONE;
    const users = usersQuery.data ?? NONE;

    const userById = useMemo(() => Object.fromEntries(users.map((u) => [u._id, u])), [users]);

    const counts = useMemo(
        () => ({
            global: schedules.filter((s) => s.scheduleType !== "individual").length,
            individual: schedules.filter((s) => s.scheduleType === "individual").length,
            overnight: schedules.filter((s) => isOvernight(s.startTime, s.endTime)).length,
        }),
        [schedules],
    );

    /*
     * "Default net working day" — the net of the active GLOBAL schedule, which
     * is the number an organization actually runs on. An average across every
     * schedule would blur a part-time override into it.
     */
    const defaultNet = useMemo(() => {
        const main = schedules.find((s) => s.scheduleType !== "individual" && s.isActive);
        if (!main) return null;
        return describeShift(main.startTime, main.endTime, main.breakTimes ?? []).netMinutes;
    }, [schedules]);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();

        return schedules.filter((s) => {
            const individual = s.scheduleType === "individual";
            if (individual !== (tab === "individual")) return false;
            if (activeOnly && !s.isActive) return false;

            if (q) {
                const who = userById[s.userId]?.name ?? "";
                if (!`${s.name} ${s.description ?? ""} ${who}`.toLowerCase().includes(q)) return false;
            }
            return true;
        });
    }, [schedules, tab, search, activeOnly, userById]);

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: ["tracking-schedules"] });

    const saveSchedule = useMutation({
        mutationFn: (values) =>
            editing?._id
                ? trackingScheduleService.updateSchedule(editing._id, values)
                : trackingScheduleService.createSchedule(values),
        onSuccess: (_res, values) => {
            toast(`${values.name} ${editing?._id ? "updated" : "created"}`);
            setEditing(null);
            setFormError("");
            invalidate();
        },
        onError: (error) =>
            setFormError(apiErrorMessage(error, "Could not save this schedule. Please try again.")),
    });

    const toggleActive = useMutation({
        mutationFn: ({ schedule, isActive }) =>
            trackingScheduleService.toggleStatus(schedule._id, { isActive }),
        onSuccess: (_res, { schedule, isActive }) => {
            toast(`${schedule.name} ${isActive ? "activated" : "paused"}`);
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not change the status."), "error"),
    });

    const removeSchedule = useMutation({
        mutationFn: (schedule) => trackingScheduleService.deleteSchedule(schedule._id),
        onSuccess: (_res, schedule) => {
            toast(`${schedule.name} deleted`, "error");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not delete this schedule."), "error"),
    });

    const askDelete = async (schedule) => {
        const ok = await confirm({
            tone: "danger",
            title: `Delete ${schedule.name}?`,
            body: (
                <>
                    <p>
                        The desktop app stops using it on its next settings sync — usually within a
                        minute.
                    </p>
                    <p className="mt-1.5">
                        {schedule.scheduleType === "individual"
                            ? "That person falls back to the organization's global schedule."
                            : "With no global schedule, the app tracks whenever it is running."}
                    </p>
                </>
            ),
            okLabel: "Delete schedule",
        });
        if (ok) removeSchedule.mutate(schedule);
    };

    const loading = schedulesQuery.isPending;

    return (
        <>
            <PageChrome breadcrumb={["Schedules"]} title="Tracking Schedules" />

            <main className="space-y-5 p-4 sm:p-5">
                {/* Page head — Create sits beside the heading, as in the markup */}
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-[22px] font-semibold tracking-tight">
                            Tracking Schedules
                        </h1>
                        <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                            When the desktop app should be tracking. Breaks are excluded from the
                            working-hours target, and late starts are measured from the schedule's
                            start time.
                        </p>
                    </div>

                    {can("manageOrg") && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                setFormError("");
                                setEditing({});
                            }}
                            data-add-schedule
                        >
                            <Icon name="calendar" className="size-4" />
                            Create schedule
                        </Button>
                    )}
                </div>

                {schedulesQuery.isError && (
                    <Callout tone="danger">
                        {apiErrorMessage(schedulesQuery.error, "Failed to load schedules.")}{" "}
                        <button
                            type="button"
                            onClick={() => schedulesQuery.refetch()}
                            className="font-semibold underline"
                        >
                            Try again
                        </button>
                    </Callout>
                )}

                <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        tone="brand"
                        label="Global schedules"
                        value={counts.global}
                        icon={<Icon name="calendar" className="size-4" />}
                    />
                    <StatCard
                        tone="violet"
                        label="Per-user overrides"
                        value={counts.individual}
                        icon={<Icon name="user" className="size-4" />}
                    />
                    <StatCard
                        tone="emerald"
                        label="Default net working day"
                        value={defaultNet === null ? "—" : formatSpan(defaultNet)}
                        icon={<Icon name="clock" className="size-4" />}
                    />
                    <StatCard
                        tone="amber"
                        label="Overnight shifts"
                        value={counts.overnight}
                        icon={<Icon name="moon" className="size-4" />}
                    />
                </section>

                {counts.overnight > 0 && (
                    <Callout tone="warning" data-overnight-warning>
                        <strong className="font-semibold">
                            One or more schedules cross midnight.
                        </strong>{" "}
                        A shift that ends on the next calendar day is counted against the day it{" "}
                        <em>started</em>. Check daily reports for these people after any change —
                        overnight spans are the one case where day-boundary bugs show up.
                    </Callout>
                )}

                {!loading && schedules.length > 0 && !schedules.some((s) => s.isActive) && (
                    <Callout tone="warning" data-none-active>
                        <strong className="font-semibold">No schedule is active.</strong> The desktop
                        app falls back to tracking whenever it is running.
                    </Callout>
                )}

                <Card>
                    <CardHead>
                        <CardTitle>
                            <Tabs
                                value={tab}
                                onChange={setTab}
                                options={[
                                    { value: "global", label: `Global ${counts.global}` },
                                    { value: "individual", label: `Per user ${counts.individual}` },
                                ]}
                            />
                        </CardTitle>
                        <CardExtra>
                            <label className="flex items-center gap-2 text-[12.5px] text-slate-500 dark:text-slate-400">
                                <Switch
                                    checked={activeOnly}
                                    onChange={(e) => setActiveOnly(e.target.checked)}
                                    aria-label="Active only"
                                />
                                Active only
                            </label>
                            <SearchInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search schedule or person…"
                                aria-label="Search schedules"
                                className="w-full sm:w-60"
                            />
                        </CardExtra>
                    </CardHead>

                    <CardBody className="p-0">
                        {loading ? (
                            <div className="space-y-px p-4.5">
                                {[0, 1, 2].map((i) => (
                                    <div
                                        key={i}
                                        data-skeleton
                                        className="h-20 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800/60"
                                    />
                                ))}
                            </div>
                        ) : rows.length ? (
                            <div>
                                {rows.map((schedule) => (
                                    <ScheduleRow
                                        key={schedule._id}
                                        schedule={schedule}
                                        user={userById[schedule.userId]}
                                        canManage={can("manageOrg")}
                                        onToggle={(isActive) =>
                                            toggleActive.mutate({ schedule, isActive })
                                        }
                                        onEdit={() => {
                                            setFormError("");
                                            setEditing(schedule);
                                        }}
                                        onDelete={() => askDelete(schedule)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="p-4.5">
                                <EmptyState
                                    title={
                                        schedules.length
                                            ? "No schedule matches"
                                            : "No schedules yet"
                                    }
                                    hint={
                                        schedules.length
                                            ? "Try the other tab, or clear the search."
                                            : "Without one, the desktop app tracks whenever it is running."
                                    }
                                    action={
                                        can("manageOrg") && !schedules.length ? (
                                            <Button variant="primary" onClick={() => setEditing({})}>
                                                Create the first schedule
                                            </Button>
                                        ) : null
                                    }
                                />
                            </div>
                        )}
                    </CardBody>
                </Card>
            </main>

            {editing && (
                <ScheduleFormDrawer
                    key={editing._id ?? "new"}
                    schedule={editing._id ? editing : null}
                    onClose={() => {
                        setEditing(null);
                        setFormError("");
                    }}
                    onSubmit={(values) => saveSchedule.mutate(values)}
                    saving={saveSchedule.isPending}
                    error={formError}
                />
            )}
        </>
    );
}

/* ---- One schedule, as a row --------------------------------------------- */
function ScheduleRow({ schedule, user, canManage, onToggle, onEdit, onDelete }) {
    const shift = describeShift(schedule.startTime, schedule.endTime, schedule.breakTimes ?? []);
    const breaks = schedule.breakTimes ?? [];

    return (
        <div
            data-schedule={schedule._id}
            className="flex flex-col gap-4 border-b border-slate-200 p-4.5 last:border-0 xl:flex-row xl:items-center dark:border-slate-800"
        >
            {/* Identity */}
            <div className="flex min-w-0 items-start gap-3 xl:w-72">
                {user ? (
                    <Avatar name={user.name} size="xl" />
                ) : (
                    <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300">
                        <Icon name="calendar" className="size-4.5" />
                    </span>
                )}

                <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-[14px] font-semibold">
                            {user ? user.name : schedule.name}
                        </span>
                        <Tag tone={schedule.isActive ? "emerald" : "slate"}>
                            {schedule.isActive ? "Active" : "Inactive"}
                        </Tag>
                        {shift.overnight && (
                            <Tag tone="amber" data-overnight={schedule._id}>
                                Overnight
                            </Tag>
                        )}
                    </p>
                    <p className="truncate text-[12px] text-slate-500 dark:text-slate-400">
                        {user ? schedule.name : schedule.description}
                    </p>
                </div>
            </div>

            {/* Days */}
            <span className="flex shrink-0 gap-1">
                {DAYS.map((day) => (
                    <span
                        key={day}
                        title={DAY_LABEL[day]}
                        className={cx(
                            "grid size-6 place-items-center rounded text-[10px] font-semibold",
                            schedule.days?.includes(day)
                                ? "bg-brand-500 text-white"
                                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600",
                        )}
                    >
                        {DAY_LABEL[day][0]}
                    </span>
                ))}
            </span>

            {/* Timeline */}
            <div className="min-w-0 flex-1">
                <ShiftTimeline
                    startTime={schedule.startTime}
                    endTime={schedule.endTime}
                    breakTimes={breaks}
                />
            </div>

            {/* Hours */}
            <div className="flex shrink-0 items-center gap-5">
                <div className="text-right">
                    <p className="font-mono text-[13px] font-semibold whitespace-nowrap">
                        {formatClock(schedule.startTime)} – {formatClock(schedule.endTime)}
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500">
                        {schedule.timezone}
                    </p>
                </div>
                <div className="text-right">
                    <p
                        className="font-mono text-[13px] font-semibold text-emerald-600 dark:text-emerald-400"
                        data-net={schedule._id}
                    >
                        {formatSpan(shift.netMinutes)}
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500">
                        net
                        {shift.paidMinutes + shift.unpaidMinutes
                            ? ` · ${formatSpan(shift.paidMinutes + shift.unpaidMinutes)} breaks`
                            : " · no breaks"}
                    </p>
                </div>
            </div>

            {/* Breaks + actions */}
            <div className="flex shrink-0 items-center gap-2">
                {breaks.length > 0 && (
                    <span className="flex flex-wrap gap-1">
                        {shift.breaks.map((br, i) =>
                            br.invalid ? (
                                // The server refuses a break outside the shift, so
                                // say so here rather than letting the row look fine.
                                <Tag
                                    key={i}
                                    tone="rose"
                                    data-invalid-breaks={schedule._id}
                                    title={`${br.name} falls outside the shift — the server will refuse it`}
                                >
                                    {br.name} · outside
                                </Tag>
                            ) : (
                                <Tag
                                    key={i}
                                    tone={br.isPaid ? "emerald" : "slate"}
                                    title={`${formatClock(br.startTime)}–${formatClock(br.endTime)}${br.isPaid ? " · paid" : " · unpaid"}`}
                                >
                                    {br.name}
                                </Tag>
                            ),
                        )}
                    </span>
                )}

                {canManage && (
                    <>
                        <Switch
                            checked={Boolean(schedule.isActive)}
                            onChange={(e) => onToggle(e.target.checked)}
                            aria-label={`${schedule.isActive ? "Pause" : "Activate"} ${schedule.name}`}
                            data-toggle={schedule._id}
                        />
                        <Button
                            variant="icon"
                            aria-label={`Edit ${schedule.name}`}
                            onClick={onEdit}
                            data-edit={schedule._id}
                        >
                            <Icon name="cog" className="size-4" />
                        </Button>
                        <Button
                            variant="icon"
                            aria-label={`Delete ${schedule.name}`}
                            onClick={onDelete}
                            data-delete={schedule._id}
                            className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                        >
                            <Icon name="xCircle" className="size-4" />
                        </Button>
                    </>
                )}
            </div>


        </div>
    );
}
